import { useMemo } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { KnowledgeAskCitation, KnowledgeDocumentListItem, WebCitation } from '@digital-twins/shared-types'
import { Text } from '@/components/ui/Text'
import { ThreadAssetView } from '@/lib/api'
import {
  citationHrefIndex,
  injectCitationMarkers,
} from './injectCitationMarkers'
import { createCreativeMarkdownHandlers } from './resolveCreativeMarkdown'
import { ThreadCitationMarker } from './ThreadCitationMarker'
import {
  citationSocialPost,
  socialCitationExcerpt,
  socialCitationLabel,
} from '../KnowledgeHubPage/social/SocialCitationPostMeta'
import { ThreadJudgmentNote } from './ThreadJudgmentNote'
import { groupWebCitationsByUrl, ThreadWebSourceCard } from './ThreadWebSourceCard'
import { citationVerification } from './citationVerification'
import { useSmoothedReveal } from './useSmoothedReveal'

interface ThreadAnswerBodyProps {
  text: string
  citations?: Array<KnowledgeAskCitation>
  refused?: boolean
  streaming?: boolean
  resolveLabel: (citation: KnowledgeAskCitation) => string
  onOpenCitation: (index: number, citation: KnowledgeAskCitation) => void
  /**
   * Web citations for this turn, already split out of the mixed
   * `KnowledgeAskCitation | AssistantCitation` array by
   * `partitionAssistantCitations` — never the raw array. Rendered as a
   * distinct card per URL below the prose; `citations` above stays
   * document-only, exactly as it was before web citations existed. Optional
   * and defaults to none so every pre-existing call site (all of which
   * predate web citations) compiles and renders byte-identical to today.
   */
  webCitations?: Array<WebCitation>
  /**
   * `judgment` stream event notes for this turn, in arrival order (see
   * `AssistantJudgmentEvent` in shared-types). Rendered as one quiet,
   * collapsed note after the web sources. Optional for the same reason as
   * `webCitations`.
   */
  judgmentNotes?: Array<string>
  threadAssets?: Array<ThreadAssetView>
  docsByToken?: Map<string, KnowledgeDocumentListItem>
}

// In-answer headings must say `font-sans` out loud: globals.css wires every
// h1-h3 to `--font-display` (a serif) so the heading font picker can retheme
// page titles. That display face is for titles, not for headings sitting
// inside a body of prose, so each renderer below opts back out explicitly.
//
// Weight carries the hierarchy first: `font-semibold` makes a heading read as
// a heading at a glance, even mid-scroll through a generated answer that
// stacks several of them. Size and spacing still separate the levels from
// each other, and `text-foreground` keeps headings darker than the prose
// around them.
const HEADING_BASE = 'font-sans font-semibold text-foreground first:mt-0'

// A fenced block carries `language-*` from remark; inline code carries nothing.
// Only the inline case gets the pill — the block case is styled by `pre`, which
// also neutralises any pill that a language-less fence would otherwise pick up.
function codeClassName(className?: string): string {
  if (className) {
    return className
  }
  return 'rounded bg-muted px-1 py-0.5 text-[0.9em]'
}

export function ThreadAnswerBody({
  text,
  citations = [],
  refused = false,
  streaming = false,
  resolveLabel,
  onOpenCitation,
  webCitations = [],
  judgmentNotes = [],
  threadAssets,
  docsByToken,
}: ThreadAnswerBodyProps) {
  const reveal = useSmoothedReveal(text, streaming)
  const visibleText = reveal.text

  // Markers are injected against the text actually on screen: citations only
  // arrive with the final frame, so during streaming this is a no-op and the
  // reveal never has to re-anchor offsets it has not reached yet.
  const markedText = useMemo(
    () => injectCitationMarkers(visibleText, citations).text,
    [visibleText, citations],
  )

  const webGroups = useMemo(
    () => groupWebCitationsByUrl(webCitations),
    [webCitations],
  )

  const creative = useMemo(
    () =>
      createCreativeMarkdownHandlers({
        assets: threadAssets,
        docsByToken,
        onOpenCitation,
      }),
    [threadAssets, docsByToken, onOpenCitation],
  )

  // Citations often arrive on the closing frame, while the reveal is still
  // writing the last paragraphs. Hold the cards until that animation has
  // caught up so they do not sit under an empty or half-written answer.
  const showWebSources = webGroups.length > 0 && !streaming && !reveal.revealing
  const showJudgmentNotes = judgmentNotes.length > 0 && !streaming && !reveal.revealing

  if (refused) {
    return (
      <Text size="sm" className="text-chart-warning">
        {text}
      </Text>
    )
  }

  return (
    <>
    <div
      className="max-w-prose text-base leading-[1.7] text-foreground"
      data-testid="thread-assistant-markdown"
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        urlTransform={creative.urlTransform}
        components={{
          h1: ({ children }) => (
            <h1
              className={`mb-2 mt-10 text-lg leading-tight ${HEADING_BASE}`}
              data-testid="thread-answer-heading-1"
            >
              {children}
            </h1>
          ),
          h2: ({ children }) => (
            <h2
              className={`mb-2 mt-10 text-base leading-snug ${HEADING_BASE}`}
              data-testid="thread-answer-heading-2"
            >
              {children}
            </h2>
          ),
          h3: ({ children }) => (
            <h3
              className={`mb-1 mt-9 text-[15px] leading-snug ${HEADING_BASE}`}
              data-testid="thread-answer-heading-3"
            >
              {children}
            </h3>
          ),
          h4: ({ children }) => (
            <h4
              className={`mb-1 mt-6 text-base leading-snug ${HEADING_BASE}`}
              data-testid="thread-answer-heading-4"
            >
              {children}
            </h4>
          ),
          p: ({ children }) => (
            <p className="my-4 first:mt-0 last:mb-0">{children}</p>
          ),
          strong: ({ children }) => (
            // Matches the heading weight (`font-semibold`) so a bulleted
            // `**label:**` lead-in reads at the same weight as a heading,
            // not a separate, heavier emphasis of its own.
            <strong className="font-semibold text-foreground">{children}</strong>
          ),
          ul: ({ children }) => (
            <ul className="my-4 ml-5 list-disc space-y-1 first:mt-0 last:mb-0">
              {children}
            </ul>
          ),
          ol: ({ children }) => (
            <ol className="my-4 ml-5 list-decimal space-y-1 first:mt-0 last:mb-0">
              {children}
            </ol>
          ),
          li: ({ children }) => <li className="my-1 leading-[1.7]">{children}</li>,
          hr: () => (
            <hr
              className="my-10 border-t border-border/40"
              data-testid="thread-answer-rule"
            />
          ),
          blockquote: ({ children }) => (
            <blockquote
              className="my-6 border-l-2 border-border/60 pl-4 italic text-muted-foreground"
              data-testid="thread-answer-blockquote"
            >
              {children}
            </blockquote>
          ),
          table: ({ children }) => (
            <div
              className="my-6 overflow-x-auto"
              data-testid="thread-answer-table-scroll"
            >
              <table className="w-full border-collapse text-sm">{children}</table>
            </div>
          ),
          thead: ({ children }) => (
            <thead className="border-b border-border">{children}</thead>
          ),
          tbody: ({ children }) => <tbody>{children}</tbody>,
          tr: ({ children }) => (
            <tr className="border-b border-border/30 last:border-0">
              {children}
            </tr>
          ),
          th: ({ children }) => (
            <th className="px-3 py-2 text-left font-medium">{children}</th>
          ),
          td: ({ children }) => (
            <td className="px-3 py-2 align-top">{children}</td>
          ),
          code: ({ className, children }) => (
            <code className={codeClassName(className)}>{children}</code>
          ),
          pre: ({ children }) => (
            <pre
              className="my-6 overflow-x-auto rounded-md bg-muted p-4 text-[0.9em] [&_code]:bg-transparent [&_code]:p-0 [&_code]:text-inherit"
              data-testid="thread-answer-code-block"
            >
              {children}
            </pre>
          ),
          a: ({ href, children }) => {
            const index = citationHrefIndex(href)
            if (index != null) {
              const citation = citations[index - 1]
              if (!citation) {
                return (
                  <sup className="text-[10px] text-muted-foreground">
                    {index}
                  </sup>
                )
              }

              const socialPost = citationSocialPost(citation)
              return (
                <ThreadCitationMarker
                  index={index}
                  documentLabel={
                    socialPost ? socialCitationLabel(socialPost) : resolveLabel(citation)
                  }
                  socialPost={socialPost}
                  excerpt={socialPost ? socialCitationExcerpt(citation) : undefined}
                  pageNumber={socialPost ? null : citation.pageNumber}
                  pageUnknown={citation.pageUnknown}
                  verification={citationVerification(citation)}
                  onOpen={(n) => onOpenCitation(n, citation)}
                />
              )
            }
            const custom = creative.renderLink({ href, children })
            if (custom !== undefined) {
              return custom
            }
            return (
              <a
                href={href}
                className="text-primary underline underline-offset-2"
                target="_blank"
                rel="noreferrer"
              >
                {children}
              </a>
            )
          },
          img: ({ src, alt }) => {
            const custom = creative.renderImage({ src, alt })
            if (custom !== undefined) {
              return custom
            }
            return (
              <img
                src={src}
                alt={alt ?? ''}
                className="max-w-full h-auto"
              />
            )
          },
        }}
      >
        {markedText}
      </ReactMarkdown>
      {reveal.revealing ? (
        <span
          className="ml-1 inline-block h-3 w-1 animate-pulse bg-primary align-middle"
          data-testid="thread-answer-caret"
        />
      ) : null}
    </div>
    {showWebSources ? (
      <div
        className="flex max-w-prose flex-wrap gap-1.5 pt-2 motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-1 motion-safe:duration-300"
        data-testid="thread-answer-web-sources"
      >
        {webGroups.map((group) => (
          <ThreadWebSourceCard key={group.key} group={group} />
        ))}
      </div>
    ) : null}
    {showJudgmentNotes ? (
      <ThreadJudgmentNote notes={judgmentNotes} />
    ) : null}
    </>
  )
}
